'use client';

import { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { apiBase } from '@/lib/api';

/**
 * 目的: パスワードを忘れたユーザーがメールアドレスを入力し、リセットリンクを送信する
 * 戻り値: JSX
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${apiBase()}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        throw new Error(data?.message ?? '送信に失敗しました');
      }
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='min-h-screen bg-white'>
      <Header />
      <main className='bg-washi'>
        <section className='mx-auto max-w-7xl px-6 py-16'>
          <div className='mx-auto max-w-md border border-gray-100 bg-white p-6 shadow-sm'>
            <h1 className='text-center font-serif text-2xl text-gray-800'>
              パスワードをお忘れですか
            </h1>
            <p className='mt-2 text-center text-sm text-gray-600'>
              登録したメールアドレスを入力すると、パスワードリセット用のリンクをお送りします。
            </p>
            {success ? (
              <div className='mt-6 rounded-lg bg-green-50 p-4 text-sm text-green-800'>
                <p className='font-semibold'>送信完了</p>
                <p className='mt-1'>
                  パスワードリセット用のリンクをメールで送信しました。メールをご確認ください。
                </p>
                <p className='mt-2 text-xs text-green-700'>
                  届かない場合は迷惑メールフォルダもご確認ください。
                </p>
                <Link
                  href='/login'
                  className='mt-4 inline-block text-sm font-semibold text-primary hover:underline'
                >
                  ログインへ戻る
                </Link>
              </div>
            ) : (
              <form className='mt-6 space-y-4' onSubmit={onSubmit}>
                <div>
                  <label
                    htmlFor='forgot-email'
                    className='block text-sm font-semibold text-gray-800'
                  >
                    メールアドレス
                  </label>
                  <input
                    id='forgot-email'
                    className='mt-2 w-full border border-gray-200 px-3 py-3 text-[15px]'
                    type='email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                {error && <p className='text-sm text-rose-600'>{error}</p>}
                <button
                  type='submit'
                  disabled={submitting}
                  className='mt-2 inline-flex w-full items-center justify-center bg-primary px-4 py-3 text-sm font-semibold text-white disabled:opacity-60'
                >
                  {submitting ? '送信中…' : 'リセットリンクを送信'}
                </button>
                <p className='text-center'>
                  <Link
                    href='/login'
                    className='text-sm text-gray-600 hover:text-primary hover:underline'
                  >
                    ログインへ戻る
                  </Link>
                </p>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
